/*
 * Custom-order stepper for the product page builder (sections/custom-main-product.liquid).
 *
 *   Step 1  Choose your design   -> Confirm
 *   Step 2  Quantity & delivery  -> Confirm   (locked until step 1 is confirmed)
 *   Step 3  Colours & sizes      -> Add to cart (locked until step 2 is confirmed)
 *
 * Only one step is open at a time; finished steps collapse to a one-line summary with "Edit".
 * The price breakdown (#volume-pricing-summary) lives outside the steps in its own always-open
 * card under them, so it isn't hidden inside step 2.
 */
(function () {
  'use strict';

  const LOCKED_HINT = { 2: 'Complete step 1 first', 3: 'Complete step 2 first' };
  const TITLES = { 2: 'Quantity & delivery', 3: 'Colours & sizes' };

  let steps = [];
  const done = new Set();
  let openStep = 1;

  function el(tag, cls, html) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html !== undefined) n.innerHTML = html;
    return n;
  }

  // ---- Summaries shown on a finished step ------------------------------------------------
  function designSummary() {
    const picked = Object.keys(pickedCards()).map((v) => spotMeta[v]?.label || v);
    const cards = document.querySelectorAll('#print-locations-container .print-location-card').length;
    const tool = document.getElementById('eaDesignSummary');
    const parts = [];
    if (tool && !tool.hidden && tool.textContent.trim()) parts.push('Designed in the design tool');
    if (picked.length) parts.push(picked.join(' + '));
    else if (cards) parts.push(`${cards} print location${cards === 1 ? '' : 's'}`);
    return parts.join(' · ');
  }

  function qtyDeliverySummary() {
    const qty = document.getElementById('volume-quantity-display')?.textContent.trim() || '1';
    const checked = document.querySelector('input[name="delivery-method"]:checked');
    const name = checked?.closest('label')?.querySelector('.delivery-name')?.textContent.trim() || '';
    return `${qty} unit${qty === '1' ? '' : 's'}${name ? ' · ' + name : ''}`;
  }

  // ---- Validation --------------------------------------------------------------------------
  function validate(n) {
    if (n === 1 && !designSummary()) {
      return 'Add a print location or design with the design tool first.';
    }
    return '';
  }

  // ---- Rendering -----------------------------------------------------------------------------
  function render() {
    steps.forEach(({ n, section, number, summary, edit, body, hint }) => {
      const isOpen = n === openStep;
      const isDone = done.has(n) && !isOpen;
      const isLocked = !isOpen && !done.has(n) && n > 1 && !done.has(n - 1);
      section.classList.toggle('is-open', isOpen);
      section.classList.toggle('is-done', isDone);
      section.classList.toggle('is-locked', isLocked);
      body.hidden = !isOpen;
      number.textContent = isDone ? '' : String(n);
      summary.textContent = isDone ? (n === 1 ? designSummary() : n === 2 ? qtyDeliverySummary() : '') : '';
      edit.hidden = !isDone;
      hint.hidden = !isLocked;
      hint.textContent = isLocked ? LOCKED_HINT[n] || '' : '';
      section.querySelector('.step-header').setAttribute('aria-expanded', String(isOpen));
    });
  }

  function open(n, scroll) {
    openStep = n;
    render();
    if (n === 3 && typeof window.updateQuantityTracker === 'function') window.updateQuantityTracker();
    if (scroll) {
      const target = steps.find((s) => s.n === n)?.section;
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }

  function confirm(n) {
    const err = document.querySelector(`[data-step-error="${n}"]`);
    const msg = validate(n);
    if (err) {
      err.textContent = msg;
      err.hidden = !msg;
    }
    if (msg) return;
    if (!done.has(n) && n === 1) window.eaQPPublish?.('ea_design_confirmed');
    done.add(n);
    open(Math.min(n + 1, steps.length), true);
  }

  // ---- Print-area tick picker (step 1) -------------------------------------------------------
  // Customers tick the spots they want instead of adding cards one by one. Each ticked spot is
  // backed by a normal print-location card (created through #addPrintLocation and pre-set to that
  // spot), so pricing, artwork upload and add-to-cart keep working unchanged.
  const POPULAR = ['Front Left Chest', 'Center Back', 'Full Front', 'Center Front', 'Top Back', 'Left Sleeve', 'Right Sleeve', 'Left Leg', 'Right Leg'];
  // Overlap rules live in ea-print-placements.js so the cart pop-up uses the same ones
  const pageHandle = () => decodeURIComponent((location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '');
  // The builder config is read by custom-main-product.js after DOMContentLoaded, which is later than this
  // script's first run, so hats are recognised from the section's data attribute (or the handle) instead.
  const isHatsPage = () => {
    const sec = document.querySelector('[data-builder-profile]');
    if (sec && sec.dataset.builderProfile === 'hats_beanies') return true;
    return !!(window.EAPrintPlacements && window.EAPrintPlacements.hatKindOf && window.EAPrintPlacements.hatKindOf(pageHandle()));
  };
  const conflictsWith = (a, b) => !!(window.EAPrintPlacements && window.EAPrintPlacements.conflictsWith(a, b, pageHandle()));

  let spotMeta = {};

  function pickedCards() {
    const map = {};
    document.querySelectorAll('#print-locations-container .print-location-card').forEach((card) => {
      if (card.dataset.removing) return;
      const input = card.querySelector('input[name*="_spot"]:checked');
      if (input) map[input.value] = card;
    });
    return map;
  }

  function syncPicker() {
    const picker = document.getElementById('eaSpotPicker');
    if (!picker) return;
    const picked = pickedCards();
    const values = Object.keys(picked);
    picker.querySelectorAll('.ea-spot').forEach((tile) => {
      const v = tile.dataset.spot;
      const on = values.includes(v);
      const blockedBy = !on && values.find((p) => conflictsWith(p, v));
      tile.querySelector('.ea-spot__toggle').setAttribute('aria-pressed', String(on));
      tile.classList.toggle('is-on', on);
      tile.classList.toggle('is-blocked', !!blockedBy);
      const slot = tile.querySelector('.ea-spot__controls');
      if (!on && slot.childElementCount) {
        slot.innerHTML = '';
        const P = window.EAPrintPlacements;
        if (P && P.setThumbArt) P.setThumbArt(tile.querySelector('.ea-thumb'), null);
      }
      slot.hidden = !on;
      tile.querySelector('.ea-spot__note').textContent = blockedBy ? `Overlaps ${spotMeta[blockedBy]?.label || blockedBy}` : '';
      const blockedLabel = blockedBy && (spotMeta[blockedBy]?.label || blockedBy);
      if (blockedBy) tile.querySelector('.ea-spot__toggle').title = `Overlaps ${blockedLabel}, which you've already picked. Untick ${blockedLabel} to print here.`;
      else tile.querySelector('.ea-spot__toggle').removeAttribute('title');
    });
    const count = picker.querySelector('[data-spot-count]');
    if (count) count.textContent = values.length ? `${values.length} selected` : 'Tick all that apply';
    // Label each card by its spot and swap its big controls for one compact row
    Object.entries(picked).forEach(([v, card]) => {
      card.classList.add('is-picked');
      const num = card.querySelector('.location-number');
      const meta = spotMeta[v];
      if (num && meta) num.textContent = `${meta.areaLabel} – ${meta.label}${meta.size ? ' · ' + meta.size : ''}`;
      const slot = picker.querySelector(`.ea-spot[data-spot="${CSS.escape(v)}"] .ea-spot__controls`);
      if (slot) tileControls(card, slot);
    });
  }

  // Controls inside a ticked tile: method dropdown (required) + artwork upload (optional).
  // The card's own method radios and file input stay hidden in the DOM and remain the source of
  // truth for pricing and add-to-cart; these controls just drive them.
  function tileControls(card, slot) {
    if (slot.dataset.card === card.id && slot.childElementCount) return;
    slot.dataset.card = card.id;
    const radios = Array.from(card.querySelectorAll('input[type="radio"][name*="_printing_method"]'));
    const file = card.querySelector('input[type="file"]');
    const methodName = (r) => (r.value === 'Embroidery' ? 'Embroidery' : 'Digital print');
    slot.innerHTML = `
      <label class="visually-hidden" for="${card.id}-method-select">Print method</label>
      <select id="${card.id}-method-select" required title="Embroidery needs a minimum of 10 units">${radios.map((r) => `<option value="${r.value}">${methodName(r)}</option>`).join('')}</select>
      <button type="button" class="ea-spot__upload"><span data-file-label>+ Artwork <em>(optional)</em></span></button>`;

    const select = slot.querySelector('select');
    if (radios.length < 2) { select.hidden = true; select.style.display = 'none'; } // hats: embroidery is the only method, nothing to choose
    const syncFromRadios = () => {
      const checked = radios.find((r) => r.checked);
      if (checked) select.value = checked.value;
      radios.forEach((r) => {
        const opt = select.querySelector(`option[value="${r.value}"]`);
        if (opt) opt.disabled = r.disabled;
      });
    };
    syncFromRadios();
    select.addEventListener('change', () => {
      const r = radios.find((x) => x.value === select.value);
      if (r && !r.checked) {
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    radios.forEach((r) => r.addEventListener('change', syncFromRadios));

    const label = slot.querySelector('[data-file-label]');
    const upload = slot.querySelector('.ea-spot__upload');
    // The tile's little garment shows the chosen artwork in its print area (images only)
    const thumb = slot.closest('.ea-spot') && slot.closest('.ea-spot').querySelector('.ea-thumb');
    let previewUrl = null;
    const showFile = () => {
      const f = file && file.files && file.files[0];
      label.innerHTML = f ? `<b>${f.name.replace(/</g, '&lt;')}</b>` : '+ Artwork <em>(optional)</em>';
      upload.classList.toggle('has-file', !!f);
      if (f) window.eaQPPublish?.('ea_artwork_uploaded');
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      previewUrl = f && /^image\//.test(f.type) ? URL.createObjectURL(f) : null;
      if (thumb && window.EAPrintPlacements.setThumbArt) window.EAPrintPlacements.setThumbArt(thumb, previewUrl);
    };
    upload.addEventListener('click', () => file && file.click());
    if (file) file.addEventListener('change', showFile);
    showFile();
  }

  function addSpot(value) {
    const meta = spotMeta[value];
    const addBtn = document.getElementById('addPrintLocation');
    const container = document.getElementById('print-locations-container');
    if (!meta || !addBtn || !container) return;
    addBtn.click();
    const cards = container.querySelectorAll('.print-location-card');
    const card = cards[cards.length - 1];
    if (!card) return;
    const select = card.querySelector('select.area-select');
    if (select) select.value = meta.area;
    if (typeof window.updateSpotOptions === 'function') window.updateSpotOptions(card.id, meta.area);
    const input = Array.from(card.querySelectorAll('input[name*="_spot"]')).find((i) => i.value === value);
    if (input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncPicker();
  }

  function removeSpot(value) {
    const card = pickedCards()[value];
    if (!card) return;
    card.dataset.removing = '1'; // untick straight away; the card itself fades out over 300ms
    syncPicker();
    if (typeof window.removeLocation === 'function') window.removeLocation(card.id);
    else card.remove();
  }

  function initSpotPicker() {
    const P = window.EAPrintPlacements;
    const section = document.getElementById('printLocationsSection');
    const container = document.getElementById('print-locations-container');
    const isHats = isHatsPage();
    if (!P || !P.areasFor || !section || !container || document.getElementById('eaSpotPicker')) return;

    const handle = decodeURIComponent((location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '');
    const areas = P.areasFor(handle);
    const AREA_LABEL = { Front: 'Front', Back: 'Back', Sleeves: 'Sleeve', Sides: 'Side', 'Embroidery placement': 'Embroidered' };
    spotMeta = {};
    areas.forEach((a) => a.spots.forEach((sp) => {
      // Hats have a single embroidery price, so the tile shows no size band
      spotMeta[sp.value] = { value: sp.value, label: sp.label, area: a.id, areaLabel: AREA_LABEL[a.id] || a.id, view: sp.view || a.view, size: isHats ? '' : P.getSize ? P.getSize(sp.value) : '' };
    }));

    const tile = (v) => {
      const m = spotMeta[v];
      return `<div class="ea-spot" data-spot="${v}">
          <button type="button" class="ea-spot__toggle" aria-pressed="false">
            <span class="ea-spot__tick" aria-hidden="true"></span>
            ${P.thumbHTML ? P.thumbHTML(v, m.view, null, 'ea-spot__thumb', handle) : `<img src="${P.svg(v, m.view)}" alt="" loading="lazy">`}
            <span class="ea-spot__label">${m.label}</span>
            <span class="ea-spot__meta">${m.areaLabel}${m.size ? ' · ' + m.size : ''}</span>
            <span class="ea-spot__note"></span>
          </button>
          <div class="ea-spot__controls" hidden></div>
        </div>`;
    };
    // Few spots (sports bra cups, pant legs, hats): show them all up front instead of hiding some under "More"
    const allSpots = areas.flatMap((a) => a.spots.map((sp) => sp.value));
    const popular = isHats || allSpots.length <= 6 ? allSpots : POPULAR.filter((v) => spotMeta[v]);
    const rest = areas
      .map((a) => ({ a, spots: a.spots.map((sp) => sp.value).filter((v) => !popular.includes(v)) }))
      .filter((g) => g.spots.length);

    const picker = el('div', 'ea-spot-picker');
    picker.id = 'eaSpotPicker';
    picker.innerHTML = `
      <div class="ea-spot-picker__head">
        <p class="ea-spot-picker__title">${isHats ? 'Where should we embroider?' : 'Where should we print?'}</p>
        <span class="ea-spot-picker__count" data-spot-count>Tick all that apply</span>
      </div>
      <p class="ea-spot-picker__group">Most popular</p>
      <div class="ea-spot-grid">${popular.map(tile).join('')}</div>
      ${rest.length ? `<details class="ea-spot-more">
        <summary>More print areas</summary>
        ${rest.map((g) => `<p class="ea-spot-picker__group">${g.a.id}</p><div class="ea-spot-grid">${g.spots.map(tile).join('')}</div>`).join('')}
      </details>` : ''}`;
    section.insertBefore(picker, container);
    section.classList.add('has-spot-picker');

    picker.addEventListener('click', (e) => {
      const toggle = e.target.closest('.ea-spot__toggle');
      const t = toggle && toggle.closest('.ea-spot');
      if (!t || t.classList.contains('is-blocked')) return;
      if (toggle.getAttribute('aria-pressed') === 'true') removeSpot(t.dataset.spot);
      else { addSpot(t.dataset.spot); window.eaQPPublish?.('ea_print_location_selected', { location: t.dataset.spot }); }
    });
    // Cards removed with their own "Remove" button untick their tile
    new MutationObserver(() => syncPicker()).observe(container, { childList: true });
    container.addEventListener('change', () => syncPicker());
    syncPicker();
  }

  // ---- Price summary -------------------------------------------------------------------------
  // #volume-pricing-summary moves out of step 2 into its own always-open card under the steps.
  function initDock() {
    const dock = document.getElementById('builderPriceDock');
    const summary = document.getElementById('volume-pricing-summary');
    if (!dock || !summary) return;
    dock.querySelector('.builder-price-dock__body').appendChild(summary);
    dock.hidden = false;
  }

  // ---- Setup -----------------------------------------------------------------------------------
  function init() {
    const root = document.getElementById('custom-product-view');
    if (!root || root.dataset.stepperReady) return;
    const sections = Array.from(root.querySelectorAll('[data-builder-step]'));
    if (sections.length < 3) return;
    root.dataset.stepperReady = '1';
    root.classList.add('has-builder-stepper');
    let customiseStarted = false;
    root.addEventListener('click', (e) => {
      if (!customiseStarted && e.target.closest('#uploadFilesToggle, #designNowBtn')) {
        customiseStarted = true; window.eaQPPublish?.('ea_customise_start');
      }
    });

    steps = sections.map((section) => {
      const n = Number(section.dataset.builderStep);
      const header = section.querySelector('.step-header');
      const number = header.querySelector('.step-number');
      const isHats = isHatsPage();
      if (TITLES[n] && !isHats) {
        const t = header.querySelector('.step-title');
        if (t) t.textContent = TITLES[n];
      }

      // Everything except the header goes into a collapsible body
      const body = el('div', 'builder-step-body');
      Array.from(section.childNodes).forEach((node) => {
        if (node !== header) body.appendChild(node);
      });
      section.appendChild(body);

      const side = el('div', 'builder-step-side');
      const summary = el('span', 'builder-step-summary');
      const hint = el('span', 'builder-step-hint');
      const edit = el('button', 'builder-step-edit', 'Edit');
      edit.type = 'button';
      side.append(summary, hint, edit);
      header.appendChild(side);
      header.setAttribute('role', 'button');
      header.tabIndex = 0;

      const reopen = () => {
        if (n === 1 || done.has(n) || done.has(n - 1)) open(n, false);
      };
      header.addEventListener('click', (e) => {
        if (e.target.closest('a, input, select')) return;
        reopen();
      });
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          reopen();
        }
      });
      return { n, section, number, summary, edit, body, hint };
    });

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-step-confirm]');
      if (btn) confirm(Number(btn.dataset.stepConfirm));
    });

    initDock();
    initSpotPicker();
    render();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
